<template>
  <AppLayout>
    <div class="paciente">
      <!-- LOADING Y ERROR -->
      <div v-if="store.loading" style="text-align:center; margin: 32px 0;">
        <span>Cargando paciente...</span>
      </div>
      <div v-if="store.error" style="color:#e74c3c; text-align:center; margin: 16px 0;">
        {{ store.error }}
        <br v-if="store.modoOffline" />
        <button
          v-if="store.modoOffline"
          @click="store.reintentarConexion()"
          style="margin-top:8px; padding:6px 16px; background:#678AB2; color:white; border:none; border-radius:6px; cursor:pointer; font-weight:600; font-size:13px;"
        >
          Reintentar conexión
        </button>
      </div>

      <!-- SIEMPRE MOSTRAR UI -->
      <div v-if="paciente" class="paciente-sections">
        <HeaderPaciente
          :nombre="paciente.nombre"
          :imageSrc="paciente.foto || PLACEHOLDER_IMG"
          :alergias="paciente.alergias || ''"
          :enfermedades="paciente.enfermedades || ''"
          :observaciones="paciente.observaciones || ''"
          :alertaAlergias="paciente.alertaAlergias || ''"
          :alertaEnfermedades="paciente.alertaEnfermedades || ''"
          :alertaObservaciones="paciente.alertaObservaciones || ''"
          azul
          @ver-imagen="abrirModalImagen"
          @subir-foto="subirFoto"
          @guardar-antecedentes="(datos) => store.editarPaciente(paciente.id, datos)"
          class="section"
        />

        <DatosPaciente :paciente="paciente" class="section" />

        <TurnosPaciente :turnos="turnosOrdenados" @agregar="agregarTurno" class="section" />

        <HistoriaClinica
          :notas="notasMostradas"
          :comentarios="paciente.comentarios || []"
          class="section"
        />

        <ArchivosPaciente
          :archivos="paciente.archivos"
          :usuarioId="paciente.usuarioId"
          :pacienteId="String(paciente.id)"
          @agregar="agregarArchivo"
          class="section"
        />

        <PresupuestoPaciente :paciente="paciente" class="section" />

        <div class="danger-zone">
          <button
            v-if="usuariosStore.usuarioActual?.exportarFichaHabilitado"
            class="btn-exportar-ficha"
            @click="exportarFicha"
          >🖨️ Exportar ficha (PDF)</button>
          <button class="btn-disable" @click="toggleEstado" :class="{ 'btn-enable': paciente.activo === false }">
            {{ paciente.activo === false ? '✅ Habilitar Paciente' : '🚫 Deshabilitar Paciente' }}
          </button>
        </div>

        <div v-if="showImageModal" class="overlay-img" @click.self="showImageModal = false">
          <div class="img-modal">
            <img :src="paciente.foto" />
          </div>
        </div>
      </div>
      <div v-else-if="!store.loading">
        <p>Paciente no encontrado</p>
      </div>
    </div>

    <!-- Los avisos y confirmaciones salen por el diálogo global (ConfirmDialog en App.vue). -->
  </AppLayout>
</template>

<script setup>
import AppLayout from "@/layouts/AppLayout.vue";
import HeaderPaciente from "@/components/HeaderPaciente.vue";
import DatosPaciente from "@/components/DatosPaciente.vue";
import TurnosPaciente from "@/components/TurnosPaciente.vue";
import HistoriaClinica from "@/components/HistoriaClinica.vue";
import ArchivosPaciente from "@/components/ArchivosPaciente.vue";
import PresupuestoPaciente from "@/components/PresupuestoPaciente.vue";

import { useRoute, useRouter } from "vue-router";
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { usePacientesStore } from "@/stores/pacientes";
import { useUsuariosStore } from "@/stores/usuarios";
import { usePerfilPanel } from "@/composables/usePerfilPanel";
import { useToast } from "@/composables/useToast";
import { exportarFichaPacientePDF } from "@/utils/exportarDatos";
import { PLACEHOLDER_PACIENTE } from "@/constants/imagenes";
import { useDialog } from "@/composables/useDialog";

const route = useRoute();
const router = useRouter();
const store = usePacientesStore();
const usuariosStore = useUsuariosStore();
const { abrir: abrirPerfil } = usePerfilPanel();
const { error: toastError } = useToast();
const { confirmar } = useDialog();

const showImageModal = ref(false);

// Swipe desde borde izquierdo para abrir el panel de perfil
let touchStartX = 0;
let touchStartY = 0;

function onTouchStart(e) {
  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
}

function onTouchEnd(e) {
  const dx = e.changedTouches[0].clientX - touchStartX;
  const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
  // Solo abrir el panel si el gesto EMPIEZA pegado al borde derecho (deslizar a propósito
  // desde el borde). Antes se disparaba con cualquier deslizamiento de derecha a izquierda
  // —incluido el scroll de tablas— y abría el panel sin querer, sobre todo en iPhone.
  const empiezaEnBordeDerecho = touchStartX > window.innerWidth - 32;
  if (empiezaEnBordeDerecho && dx < -70 && dy < 60) {
    abrirPerfil();
  }
}

onMounted(() => {
  document.addEventListener('touchstart', onTouchStart, { passive: true });
  document.addEventListener('touchend', onTouchEnd, { passive: true });
});

onUnmounted(() => {
  document.removeEventListener('touchstart', onTouchStart);
  document.removeEventListener('touchend', onTouchEnd);
});

const PLACEHOLDER_IMG = PLACEHOLDER_PACIENTE;

onMounted(() => {
  if (!store.pacientes.length) {
    store.cargarDesdeLocalStorage();
  }
});

const paciente = computed(() =>
  store.pacientes.find((p) => p.id === Number(route.params.id)),
);

// Fase 2: escuchar la subcolección `historia` del paciente abierto (se re-suscribe si
// cambia el id de la ruta) y soltarla al salir.
watch(
  () => [route.params.id, usuariosStore.usuarioActual?.id || usuariosStore.usuarioActual?.uid],
  ([id, uid]) => { if (id != null && uid) store.escucharHistoria(Number(id)); },
  { immediate: true }
);
onUnmounted(() => store.detenerEscuchaHistoria());

// Evoluciones a mostrar = array viejo (lo que aún no se migró) + subcolección, mezcladas
// y ordenadas por fecha. Dedup por id por si una evolución ya está en los dos lados.
const notasMostradas = computed(() => {
  const sub = store.historiaActual || [];
  const idsSub = new Set(sub.map((n) => n.id));
  // Para notas viejas sin id, deduplicamos por fecha+texto (así, una evolución vieja ya
  // copiada a la subcolección no aparece dos veces tras la migración).
  const clavesSub = new Set(sub.map((n) => `${n.fecha}||${n.texto}`));
  const legacy = (paciente.value?.notas || []).filter(
    (n) => !(idsSub.has(n.id) || clavesSub.has(`${n.fecha}||${n.texto}`))
  );
  return [...legacy, ...sub].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
});

const turnosOrdenados = computed(() => {
  if (!paciente.value?.turnos) return [];
  
  return [...paciente.value.turnos].sort((a, b) => {
    // Ordenar por fecha (más reciente primero)
    const fechaComparacion = b.fecha.localeCompare(a.fecha);
    if (fechaComparacion !== 0) return fechaComparacion;
    
    // Si es la misma fecha, ordenar por hora (más reciente primero)
    const horaA = a.inicio || a.hora || '00:00';
    const horaB = b.inicio || b.hora || '00:00';
    return horaB.localeCompare(horaA);
  });
});

function agregarTurno() {
  // Redirigir a la agenda (vista mes) con el paciente preseleccionado. Pasamos también el
  // id, no solo el nombre: así, si hay homónimos, el turno se engancha a ESTA ficha exacta.
  router.push({
    path: '/agenda',
    query: {
      view: 'mes',
      paciente: paciente.value.nombre,
      pacienteId: String(paciente.value.id)
    }
  });
}

async function agregarArchivo(file, callback) {
  if (!file || !paciente.value) return;
  try {
    await store.agregarArchivo(paciente.value.id, file);
    callback?.(true);
  } catch (e) {
    callback?.(false);
  }
}


// Achica y comprime la imagen antes de guardarla. Las fotos de cámara del iPhone pesan
// varios MB y, guardadas enteras dentro de la ficha, superaban el límite de tamaño del
// documento en Firestore (1 MB) → el guardado fallaba. Reducimos a máx 800px y JPEG de
// calidad media, lo que baja la foto a ~80KB y la hace guardable de verdad.
function comprimirImagen(file, maxLado = 800, calidad = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > maxLado || height > maxLado) {
          if (width >= height) {
            height = Math.round((height * maxLado) / width);
            width = maxLado;
          } else {
            width = Math.round((width * maxLado) / height);
            height = maxLado;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", calidad));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

async function subirFoto(file) {
  if (!file) return;
  try {
    const dataUrl = await comprimirImagen(file);
    // La foto se sube a Storage y en el documento queda solo la URL (ver el store).
    await store.actualizarFotoPaciente(paciente.value.id, dataUrl);
  } catch (e) {
    console.warn("No se pudo procesar la imagen:", e);
    toastError("No se pudo procesar la imagen. Probá con otra foto.");
  }
}

function abrirModalImagen() {
  if (paciente.value.foto) {
    showImageModal.value = true;
  }
}

function exportarFicha() {
  if (!paciente.value) return;
  try {
    exportarFichaPacientePDF(
      paciente.value,
      notasMostradas.value,
      usuariosStore.usuarioActual || {}
    );
  } catch (e) {
    console.warn("No se pudo exportar la ficha:", e);
    toastError(e.message || "No se pudo generar la ficha.");
  }
}

function toggleEstado() {
  const accion = paciente.value.activo === false ? "habilitar" : "deshabilitar";
  const textoBoton = paciente.value.activo === false ? "Sí, habilitar" : "Sí, deshabilitar";
  confirmar(`¿Estás seguro de que quieres ${accion} a este paciente?`, () => {
    store.toggleEstadoPaciente(paciente.value.id);
  }, { textoBoton, peligro: true });
}
</script>

<style scoped>
.paciente {
  padding: 16px;
  padding-bottom: 80px;
  background: #f2f4f7;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: system-ui, sans-serif;
}

.paciente-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section {
  margin: 0;
}

.btn-exportar-ficha {
  display: block;
  margin: 0 auto 12px;
  background: #eaf1ff;
  color: #678AB2;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}

.btn-exportar-ficha:hover {
  background: #dce8fb;
}

.editar {
  padding: 10px;
  border-radius: 10px;
  border: none;
  background: #eaf1ff;
  color: #678AB2;
  font-weight: 600;
  text-align: center;
}

.overlay-img {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.img-modal img {
  width: 90%;
  max-width: 420px;
  border-radius: 12px;
}

/* Tablet */
@media (min-width: 768px) {
  .paciente {
    padding: 24px;
    gap: 20px;
  }

  .paciente-sections {
    gap: 20px;
  }

  .img-modal img {
    max-width: 600px;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .paciente {
    padding: 32px;
    max-width: 1200px;
    margin: 0 auto;
    gap: 24px;
  }

  .paciente-sections {
    gap: 24px;
  }

  .img-modal img {
    max-width: 800px;
  }
}

.danger-zone {
  margin-top: 30px;
  text-align: center;
}

/* Notebook: reduce espacios para que entren más secciones en pantalla */
@media (min-width: 900px) and (max-height: 820px) {
  .paciente {
    padding: 16px 24px;
    gap: 12px;
  }

  .paciente-sections {
    gap: 12px;
  }
}

.btn-disable {
  background: #e0e0e0;
  color: #555;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn-enable {
  background: #d1fae5;
  color: #065f46;
}

</style>
